/**
 * Mobile web3 entry: push users into Phantom’s in-app browser for full-screen dApp
 * (no address-bar waste). Dismissible; never blocks if already in Phantom / PWA.
 */
import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { Wallet, Smartphone, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  isInPhantomInAppBrowser,
  isMobileBrowser,
  openDappInPhantomBrowser,
} from "@/lib/mobile-web3";
import { BRAND } from "@shared/brand";

const DISMISS_KEY = "budb_phantom_entry_dismiss_v1";

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    Boolean((navigator as Navigator & { standalone?: boolean }).standalone)
  );
}

export function PhantomMobileEntry() {
  const [path] = useLocation();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!isMobileBrowser()) return;
    if (isStandalone()) return;
    if (isInPhantomInAppBrowser()) return;
    try {
      if (sessionStorage.getItem(DISMISS_KEY) === "1") return;
    } catch {
      /* ignore */
    }
    // Don’t block mid-table; only shell routes
    if (path.startsWith("/table")) return;
    setShow(true);
  }, [path]);

  if (!show) return null;

  const openPhantom = () => {
    const returnUrl = `https://poker.grudge-studio.com${path || "/lobby"}`;
    openDappInPhantomBrowser(returnUrl);
  };

  const dismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
    setShow(false);
  };

  return (
    <div
      className="phantom-mobile-entry"
      role="dialog"
      aria-label="Open in Phantom"
    >
      <div className="phantom-mobile-entry__card">
        <button
          type="button"
          className="phantom-mobile-entry__x"
          aria-label="Continue in this browser"
          onClick={dismiss}
        >
          <X size={18} />
        </button>
        <img
          src={BRAND.logoMark || "/media/budb-pkr-logo-mark.jpg"}
          alt=""
          className="phantom-mobile-entry__logo"
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = "none";
          }}
        />
        <h2 className="phantom-mobile-entry__title">Play full-screen in Phantom</h2>
        <p className="phantom-mobile-entry__body">
          Mobile Safari/Chrome shows a URL bar and wastes height. Opening in the{" "}
          <strong>Phantom app browser</strong> gives a clean dApp surface, inject
          wallet, and easier fund / sign flows.
        </p>
        <Button
          className="w-full h-12 bg-violet-600 hover:bg-violet-500 font-bold text-base"
          onClick={openPhantom}
        >
          <Wallet className="mr-2 h-5 w-5" />
          Open in Phantom
        </Button>
        <Button
          variant="ghost"
          className="w-full h-11 text-zinc-400"
          onClick={dismiss}
        >
          <Smartphone className="mr-2 h-4 w-4" />
          Stay in this browser
        </Button>
        <p className="phantom-mobile-entry__hint">
          Tip: Install <strong>Add to Home Screen</strong> for chrome-free PWA
          mode after you connect once.
        </p>
      </div>
    </div>
  );
}
